import {
  CrossingEraseCommand,
  GridFlowCoreError,
  StartPathAtNodeCommand,
  TruncatePathCommand,
  checkWinCondition,
  createPathMoveCommand,
  getBacktrackPoint,
  getCell,
  getPathState,
  getTail,
  pointsEqual,
  type GridPoint,
  type LevelState,
  type PathCommand
} from "@gridflow/core";

export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

export interface BoardGeometry {
  readonly offsetX: number;
  readonly offsetY: number;
  readonly cellSize: number;
  readonly width: number;
  readonly height: number;
  readonly snapRadiusRatio?: number;
}

export interface HapticFeedback {
  selectionChanged(): void | Promise<void>;
  warning(): void | Promise<void>;
  impact(): void | Promise<void>;
}

export interface VibrationLike {
  vibrate(pattern: VibratePattern): boolean;
}

export interface GridInputControllerOptions {
  readonly haptics?: HapticFeedback;
}

export type GridInputRejectReason =
  | "inactive"
  | "outside-board"
  | "outside-snap-radius"
  | "empty-start"
  | "obstacle"
  | "invalid-path";

export interface GridInputResult {
  readonly accepted: boolean;
  readonly reason: GridInputRejectReason | null;
  readonly activeColor: string | null;
  readonly gridPoint: GridPoint | null;
  readonly commandsExecuted: number;
  readonly hasWon: boolean;
}

interface ActiveInputSession {
  readonly color: string;
  lastGridPoint: GridPoint;
}

export function screenToGrid(screen: ScreenPoint, geometry: BoardGeometry): GridPoint | null {
  assertValidGeometry(geometry);
  const x = Math.floor((screen.x - geometry.offsetX) / geometry.cellSize);
  const y = Math.floor((screen.y - geometry.offsetY) / geometry.cellSize);
  if (x < 0 || x >= geometry.width || y < 0 || y >= geometry.height) {
    return null;
  }
  return { x, y };
}

export function gridCellCenter(point: GridPoint, geometry: BoardGeometry): ScreenPoint {
  assertValidGeometry(geometry);
  return {
    x: geometry.offsetX + point.x * geometry.cellSize + geometry.cellSize / 2,
    y: geometry.offsetY + point.y * geometry.cellSize + geometry.cellSize / 2
  };
}

export function isWithinSnapRadius(
  screen: ScreenPoint,
  point: GridPoint,
  geometry: BoardGeometry
): boolean {
  const center = gridCellCenter(point, geometry);
  const dx = screen.x - center.x;
  const dy = screen.y - center.y;
  return Math.hypot(dx, dy) <= geometry.cellSize * getSnapRadiusRatio(geometry);
}

export function snapScreenToGrid(screen: ScreenPoint, geometry: BoardGeometry): GridPoint | null {
  const point = screenToGrid(screen, geometry);
  if (!point || !isWithinSnapRadius(screen, point, geometry)) {
    return null;
  }
  return point;
}

export function interpolateGridLine(from: GridPoint, to: GridPoint): GridPoint[] {
  const points: GridPoint[] = [{ ...from }];
  let current = { ...from };

  while (!pointsEqual(current, to)) {
    const dx = to.x - current.x;
    const dy = to.y - current.y;
    if (Math.abs(dx) >= Math.abs(dy) && dx !== 0) {
      current = { x: current.x + Math.sign(dx), y: current.y };
    } else if (dy !== 0) {
      current = { x: current.x, y: current.y + Math.sign(dy) };
    } else {
      current = { x: current.x + Math.sign(dx), y: current.y };
    }
    points.push({ ...current });
  }

  return points;
}

export function createNoopHaptics(): HapticFeedback {
  return {
    selectionChanged() {},
    warning() {},
    impact() {}
  };
}

export function createWebVibrationHaptics(
  vibrator: VibrationLike | null | undefined = globalThis.navigator
): HapticFeedback {
  return {
    selectionChanged() {
      vibrator?.vibrate(8);
    },
    warning() {
      vibrator?.vibrate([12, 24, 12]);
    },
    impact() {
      vibrator?.vibrate(18);
    }
  };
}

export interface CapacitorHapticsLike {
  selectionChanged(): void | Promise<void>;
  impact(options?: { style?: "LIGHT" | "MEDIUM" | "HEAVY" }): void | Promise<void>;
  notification(options?: { type?: "SUCCESS" | "WARNING" | "ERROR" }): void | Promise<void>;
}

export function createCapacitorHapticsAdapter(haptics: CapacitorHapticsLike): HapticFeedback {
  return {
    selectionChanged() {
      return haptics.selectionChanged();
    },
    warning() {
      return haptics.notification({ type: "WARNING" });
    },
    impact() {
      return haptics.impact({ style: "LIGHT" });
    }
  };
}

export class GridInputController {
  private activeSession: ActiveInputSession | null = null;
  private readonly movementCommands: PathCommand[] = [];
  private readonly haptics: HapticFeedback;
  private executedCommandCount = 0;

  public constructor(
    private readonly state: LevelState,
    private readonly geometry: BoardGeometry,
    options: GridInputControllerOptions = {}
  ) {
    assertValidGeometry(geometry);
    this.haptics = options.haptics ?? createNoopHaptics();
  }

  public touchStart(screen: ScreenPoint): GridInputResult {
    const point = snapScreenToGrid(screen, this.geometry);
    if (!point) {
      return this.reject("outside-snap-radius", null);
    }

    const cell = getCell(this.state, point);
    if (cell.isObstacle) {
      this.haptics.warning();
      return this.reject("obstacle", point);
    }

    if (cell.hasNode && cell.nodeColor !== null) {
      const command = new StartPathAtNodeCommand(this.state, cell.nodeColor, point);
      command.execute();
      this.executedCommandCount += 1;
      this.activeSession = {
        color: cell.nodeColor,
        lastGridPoint: point
      };
      this.haptics.selectionChanged();
      return this.accept(point);
    }

    if (cell.occupiedByColor !== null && cell.pathIndex !== null) {
      const color = cell.occupiedByColor;
      const path = getPathState(this.state, color);
      if (cell.pathIndex + 1 < path.cells.length) {
        const command = new TruncatePathCommand(this.state, color, cell.pathIndex + 1);
        command.execute();
        this.executedCommandCount += 1;
      }

      this.activeSession = {
        color,
        lastGridPoint: point
      };
      this.haptics.selectionChanged();
      return this.accept(point);
    }

    return this.reject("empty-start", point);
  }

  public touchMove(screen: ScreenPoint): GridInputResult {
    const point = snapScreenToGrid(screen, this.geometry);
    if (!point) {
      return this.reject("outside-snap-radius", null);
    }
    return this.moveToGridPoint(point, true);
  }

  public moveToGridPoint(point: GridPoint, interpolate: boolean): GridInputResult {
    if (!this.activeSession) {
      return this.reject("inactive", point);
    }

    const sequence = interpolate
      ? interpolateGridLine(this.activeSession.lastGridPoint, point).slice(1)
      : [point];

    let lastAccepted: GridPoint | null = null;
    for (const nextPoint of sequence) {
      const result = this.applyGridStep(nextPoint);
      if (!result.accepted) {
        return result;
      }
      lastAccepted = nextPoint;
    }

    return this.accept(lastAccepted ?? this.activeSession.lastGridPoint);
  }

  public touchEnd(): GridInputResult {
    const point = this.activeSession?.lastGridPoint ?? null;
    this.activeSession = null;
    this.movementCommands.length = 0;
    return {
      accepted: true,
      reason: null,
      activeColor: null,
      gridPoint: point,
      commandsExecuted: this.executedCommandCount,
      hasWon: checkWinCondition(this.state)
    };
  }

  public get activeColor(): string | null {
    return this.activeSession?.color ?? null;
  }

  public get commandsExecuted(): number {
    return this.executedCommandCount;
  }

  private applyGridStep(point: GridPoint): GridInputResult {
    if (!this.activeSession) {
      return this.reject("inactive", point);
    }

    const path = getPathState(this.state, this.activeSession.color);
    if (pointsEqual(getTail(path), point)) {
      this.activeSession.lastGridPoint = point;
      return this.accept(point);
    }

    const backtrackPoint = getBacktrackPoint(path);
    if (backtrackPoint && pointsEqual(backtrackPoint, point)) {
      const command = this.movementCommands.pop();
      if (!command) {
        return this.reject("invalid-path", point);
      }
      command.undo();
      this.activeSession.lastGridPoint = getTail(
        getPathState(this.state, this.activeSession.color)
      );
      this.haptics.selectionChanged();
      return this.accept(point);
    }

    try {
      const command = createPathMoveCommand(this.state, this.activeSession.color, point);
      command.execute();
      this.executedCommandCount += 1;
      this.movementCommands.push(command);
      this.activeSession.lastGridPoint = getTail(
        getPathState(this.state, this.activeSession.color)
      );
      if (command instanceof CrossingEraseCommand) {
        this.haptics.impact();
      } else {
        this.haptics.selectionChanged();
      }
      return this.accept(point);
    } catch (error) {
      if (error instanceof GridFlowCoreError) {
        this.haptics.warning();
        return this.reject("invalid-path", point);
      }
      throw error;
    }
  }

  private accept(point: GridPoint | null): GridInputResult {
    return {
      accepted: true,
      reason: null,
      activeColor: this.activeColor,
      gridPoint: point,
      commandsExecuted: this.executedCommandCount,
      hasWon: checkWinCondition(this.state)
    };
  }

  private reject(reason: GridInputRejectReason, point: GridPoint | null): GridInputResult {
    return {
      accepted: false,
      reason,
      activeColor: this.activeColor,
      gridPoint: point,
      commandsExecuted: this.executedCommandCount,
      hasWon: checkWinCondition(this.state)
    };
  }
}

function getSnapRadiusRatio(geometry: BoardGeometry): number {
  return geometry.snapRadiusRatio ?? 0.4;
}

function assertValidGeometry(geometry: BoardGeometry): void {
  if (
    !Number.isFinite(geometry.offsetX) ||
    !Number.isFinite(geometry.offsetY) ||
    !Number.isFinite(geometry.cellSize) ||
    !Number.isInteger(geometry.width) ||
    !Number.isInteger(geometry.height) ||
    geometry.cellSize <= 0 ||
    geometry.width <= 0 ||
    geometry.height <= 0 ||
    getSnapRadiusRatio(geometry) <= 0
  ) {
    throw new Error("Invalid board geometry.");
  }
}
